import { Activity, Building2, Users } from 'lucide-react';
import { useState } from 'react';
import { ChangesPerDayChart } from '../components/ChangesPerDayChart';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useChangesPerDay, useDashboardStats } from '../hooks/useMonitoringStats';
import type { TimeWindow } from '../types/domain';

function StatCard({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  description?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon size={16} className="text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  );
}

export function Dashboard() {
  const [windowDays, setWindowDays] = useState<TimeWindow>(30);
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const {
    data: changesPerDay,
    isLoading: chartLoading,
    isError: chartError,
  } = useChangesPerDay(windowDays);

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold">Dashboard</h1>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton cards
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 w-24 animate-pulse rounded bg-muted" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-12 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <StatCard title="Total Orgs" value={stats?.orgs.total ?? 0} icon={Building2} />
            <StatCard
              title="Orgs Deshabilitadas"
              value={stats?.orgs.disabled ?? 0}
              icon={Building2}
              description={`de ${stats?.orgs.total ?? 0} totales`}
            />
            <StatCard title="Total Usuarios" value={stats?.users_total ?? 0} icon={Users} />
            <StatCard
              title="Procesos Activos"
              value={stats?.processes_total ?? 0}
              icon={Activity}
            />
          </>
        )}
      </div>

      <ChangesPerDayChart
        data={changesPerDay ?? []}
        isLoading={chartLoading}
        isError={chartError}
        windowDays={windowDays}
        onWindowChange={setWindowDays}
      />
    </div>
  );
}
